<template>
  <CoreDialog
    v-if="items?.length"
    v-model="isOpened"
    data-qa="lpu_photos_dialog"
    :size="isMobile ? 'fullscreen' : 'xl'"
    :title="isMobile ? dialogTitle : undefined"
    :divider="isMobile"
    :content-class="isMobile ? undefined : 'lpu-photos-dialog-window'"
    :slot-content-class="isMobile ? undefined : 'py-8'"
  >
    <template
      v-if="isMobile"
      #prependBtn="{ btnProps }"
    >
      <v-btn
        v-bind="btnProps"
        @click="isOpened = false"
      >
        <span class="ui-icon-close-not-a-circle" />
      </v-btn>
    </template>

    <div
      class="lpu-photos-dialog d-flex flex-column ga-4"
      :class="{ 'lpu-photos-dialog--mobile flex-grow-1': isMobile }"
    >
      <CoreSwiper
        ref="swiperRef"
        class="lpu-photos-dialog__swiper"
        :initial-slide="initialSlide"
        :slides-per-view="1"
        :space-between="36"
        :has-pagination="showPagination"
        :has-navigation="showNavigation"
        :auto-height="false"
      >
        <template #slides="{ slideAttrs }">
          <div
            v-for="(item, index) in items"
            :key="`${item.id ?? item.src}-${index}`"
            v-bind="slideAttrs"
          >
            <img
              class="lpu-photos-dialog__image"
              :src="item.src"
              :alt="item.alt || item.title || ''"
              data-qa="lpu_photos_dialog_image"
            >
          </div>
        </template>
      </CoreSwiper>

      <div
        v-if="!isMobile"
        class="lpu-photos-dialog__meta d-flex flex-column align-center"
      >
        <p
          v-if="currentItem?.title"
          class="text-body-1 text--text align-self-stretch mb-0"
          data-qa="lpu_photos_dialog_title"
        >
          {{ currentItem.title }}
        </p>
        <p
          class="text-subtitle-2 text-secondary--text text-center mb-0"
          data-qa="lpu_photos_dialog_counter"
        >
          {{ activeIndex + 1 }} {{ tStatic('из') }} {{ items.length }}
        </p>
      </div>
    </div>
  </CoreDialog>
</template>

<script lang="ts" setup>
/**
 * Модалка просмотра фото клиники: слайдер, подпись и счётчик «N из M».
 *
 * Десктоп — CoreDialog xl (650px) со стрелками и мета под фото.
 * Мобилка — fullscreen: в toolbar — title текущего фото, внизу буллеты.
 * Слайдер не завязан на isOpened в v-if: иначе при закрытии контент пропадает
 * раньше leave-анимации и окно схлопывается.
 */
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type Swiper from 'swiper/core'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'

type CoreSwiperExpose = {
  swiperInstance: Swiper | null
}

const props = withDefaults(defineProps<{
  items?: CoreImageLightboxItem[]
}>(), {
  items: () => [],
})

const isMobile = useIsMobile()
const isOpened = ref(false)
const initialSlide = ref(0)
const activeIndex = ref(0)
const swiperRef = ref<CoreSwiperExpose | null>(null)

const showNavigation = computed(() => !isMobile.value && props.items.length > 1)
const showPagination = computed(() => isMobile.value && props.items.length > 1)
const currentItem = computed(() => props.items[activeIndex.value])
const dialogTitle = computed(() => currentItem.value?.title || tStatic('Фото'))

function getSwiper() {
  return swiperRef.value?.swiperInstance ?? null
}

watch(
  () => getSwiper(),
  (swiper, _prev, onCleanup) => {
    if (!swiper) {
      return
    }

    const syncIndex = () => {
      activeIndex.value = swiper.activeIndex
    }

    syncIndex()
    swiper.on('slideChange', syncIndex)

    onCleanup(() => {
      swiper.off('slideChange', syncIndex)
    })
  },
)

function open(index: number) {
  initialSlide.value = index
  activeIndex.value = index
  isOpened.value = true

  // Контент диалога может остаться после первого открытия — initialSlide уже не сработает.
  nextTick(() => {
    getSwiper()?.slideTo(index, 0)
  })
}

defineExpose({ open })
</script>

<style lang="scss">
@import 'www/themes/doctors/common/variables';

.v-dialog.lpu-photos-dialog-window {
  overflow: hidden;
  border-radius: $border-radius-xl;

  /* CoreDialog ставит rounded-sm с !important (4px). */
  .core-dialog {
    border-radius: $border-radius-xl !important;
  }
}
</style>

<style scoped lang="scss">
@import 'www/themes/doctors/common/variables';

.lpu-photos-dialog {
  $nav-btn-size: 36px;
  $image-inset: 18px;
  $image-height: 317px;

  &__swiper {
    min-height: $image-height;

    :deep(.swiper-slide) {
      width: 100% !important;
      max-width: 100%;
      box-sizing: border-box;
    }

    &:not(.core-swiper--ready) :deep(.swiper-container) {
      min-height: $image-height;
      height: $image-height;
      visibility: hidden;
    }

    :deep(.swiper-pagination-bullet-active) {
      background-color: var(--lpu-branding-color-primary, $ui-kit-primary);
    }

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
      z-index: 3;
      top: 50%;
      margin-top: 0;
      width: $nav-btn-size;
      height: $nav-btn-size;
      transform: translateY(-50%);
      box-shadow: none !important;
      background-color: var(--lpu-branding-color-primary, $ui-kit-primary) !important;
      color: $ui-kit-bg-gray-0 !important;

      span {
        font-size: 24px;
        color: $ui-kit-bg-gray-0 !important;
      }
    }

    :deep(.swiper-button-prev) {
      left: 0;
      right: auto;
    }

    :deep(.swiper-button-next) {
      right: 0;
      left: auto;
    }

    :deep(.swiper-button-disabled) {
      opacity: 0 !important;
      pointer-events: auto !important;
      cursor: default;
    }
  }

  &__image {
    display: block;
    width: 100%;
    max-height: 317px;
    object-fit: contain;
  }

  &__meta {
    padding-left: $image-inset;
    padding-right: $image-inset;
    gap: 10px;
  }
}

.lpu-photos-dialog--mobile {
  min-height: 0;
  overflow: hidden;

  .lpu-photos-dialog__swiper {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;

    :deep(.swiper-container) {
      height: 100%;
      padding-bottom: 32px;
      box-sizing: border-box;
    }

    :deep(.swiper-wrapper),
    :deep(.swiper-slide) {
      height: 100%;
    }

    :deep(.swiper-slide) {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &:not(.core-swiper--ready) :deep(.swiper-container) {
      min-height: 0;
      height: 100%;
      visibility: visible;
    }
  }

  .lpu-photos-dialog__image {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 75vh;
    max-height: 75dvh;
    object-fit: contain;
  }
}
</style>
